/**
 * Unified Employee Portal Settings Page
 *
 * Single integrated settings page with:
 * - Account Information
 * - Notification Preferences
 * - SMS Consent
 * - Privacy & Security
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  User,
  Bell,
  MessageSquare,
  Shield,
  Lock,
  Mail,
  Phone,
  CheckCircle,
  AlertCircle,
  Save,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import {
  getMemberData,
  getMemberToken,
  getSettings,
  updateSettings,
  type PortalSettings,
} from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import EmployeePortalLayout from '../layout';

interface EmployeeData {
  id: number;
  employee_number: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  department?: string;
  employer_name?: string;
}

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employee, setEmployee] = useState<EmployeeData | null>(null);

  // Settings state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [preferredContactMethod, setPreferredContactMethod] = useState('email');
  const [allowPortalMessages, setAllowPortalMessages] = useState(true);
  const [smsConsentGiven, setSmsConsentGiven] = useState(false);
  const [smsConsentDate, setSmsConsentDate] = useState<string | null>(null);

  // Password state
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const member = getMemberData();

  useEffect(() => {
    const fetchData = async () => {
      if (!member?.id) {
        setLoading(false);
        return;
      }

      try {
        const token = getMemberToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch employee data
        const employeeResponse = await fetch(
          getWeaveinApiUrl(`/api/employees/by-member/${member.id}`),
          {
            headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
          }
        );

        if (employeeResponse.ok) {
          const empData = await employeeResponse.json();
          setEmployee({
            ...empData,
            email: member.email,
            phone: member.phone_number,
          });
        }

        // Fetch portal settings
        const settings = await getSettings();
        if (settings) {
          setEmailNotifications(settings.emailNotificationsEnabled ?? true);
          setSmsNotifications(settings.smsNotificationsEnabled ?? false);
          setPreferredContactMethod(settings.preferredContactMethod ?? 'email');
          setAllowPortalMessages(settings.allowPortalMessages ?? true);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [member?.id]);

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updatedSettings: PortalSettings = {
        emailNotificationsEnabled: emailNotifications,
        smsNotificationsEnabled: smsNotifications,
        preferredContactMethod,
        allowPortalMessages,
      };

      await updateSettings(updatedSettings);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    setError('Password change is not yet implemented');
  };

  if (loading) {
    return (
      <EmployeePortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </EmployeePortalLayout>
    );
  }

  return (
    <EmployeePortalLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">
            Manage your account preferences and notification settings.
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {/* Account Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">First Name</label>
                <p className="mt-1 text-gray-900">{member?.first_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Last Name</label>
                <p className="mt-1 text-gray-900">{member?.last_name}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Email</label>
              <p className="mt-1 text-gray-900">{member?.email}</p>
            </div>
            {employee && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="text-sm font-medium text-gray-600">Employee ID</label>
                  <p className="mt-1 text-gray-900">{employee.employee_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Department</label>
                  <p className="mt-1 text-gray-900">{employee.department || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Job Title</label>
                  <p className="mt-1 text-gray-900">{employee.job_title || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Employer</label>
                  <p className="mt-1 text-gray-900">{employee.employer_name || '-'}</p>
                </div>
              </div>
            )}
            <p className="text-sm text-gray-500 pt-2">
              To update your account information, please contact your HR department.
            </p>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-2">
              <Bell className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
            </div>
          </div>
          <div className="p-6 space-y-6">
            {/* Email */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="emailNotifications"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              <div>
                <label htmlFor="emailNotifications" className="font-medium text-gray-900 flex items-center cursor-pointer">
                  <Mail className="w-4 h-4 mr-2 text-gray-500" />
                  Email Notifications
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  Receive email notifications for new messages and updates
                </p>
              </div>
            </div>

            {/* SMS */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="smsNotifications"
                checked={smsNotifications}
                onChange={(e) => setSmsNotifications(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              <div>
                <label htmlFor="smsNotifications" className="font-medium text-gray-900 flex items-center cursor-pointer">
                  <Phone className="w-4 h-4 mr-2 text-gray-500" />
                  SMS Notifications
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  Receive text message notifications for urgent messages
                </p>
              </div>
            </div>

            {/* Portal Messages */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="portalMessages"
                checked={allowPortalMessages}
                onChange={(e) => setAllowPortalMessages(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
              />
              <div>
                <label htmlFor="portalMessages" className="font-medium text-gray-900 flex items-center cursor-pointer">
                  <MessageSquare className="w-4 h-4 mr-2 text-gray-500" />
                  Portal Messages
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  Allow care team to send you messages through the portal
                </p>
              </div>
            </div>

            {/* Preferred Contact */}
            <div>
              <label className="block font-medium text-gray-900 mb-2">Preferred Contact Method</label>
              <select
                value={preferredContactMethod}
                onChange={(e) => setPreferredContactMethod(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="email">Email</option>
                <option value="sms">SMS / Text Message</option>
                <option value="phone">Phone Call</option>
                <option value="portal">Portal Messages Only</option>
              </select>
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Preferences
                </>
              )}
            </button>
          </div>
        </div>

        {/* SMS Consent */}
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 overflow-hidden">
          <div className="p-4 border-b border-blue-200 bg-blue-50">
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-blue-900">SMS Messaging Consent</h2>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2">
                By opting in to SMS notifications, you consent to receive text messages regarding
                appointments, reminders, and important updates about your care.
              </p>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>Message Frequency:</strong> Up to 10 messages per month</p>
                <p><strong>Carrier Charges:</strong> Message and data rates may apply</p>
                <p><strong>Your Rights:</strong> Text STOP to opt out at any time</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="smsConsent"
                checked={smsConsentGiven}
                onChange={(e) => {
                  setSmsConsentGiven(e.target.checked);
                  if (e.target.checked) {
                    setSmsConsentDate(new Date().toISOString());
                  }
                }}
                className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <div>
                <label htmlFor="smsConsent" className="font-medium text-gray-900 cursor-pointer">
                  I consent to receive SMS text messages
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  I understand and agree to receive text messages at the phone number on file.
                </p>
                {smsConsentGiven && smsConsentDate && (
                  <p className="text-xs text-green-600 mt-2 flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Consent given on {new Date(smsConsentDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-2">
              <Lock className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Security</h2>
            </div>
          </div>
          <div className="p-6">
            {!changePasswordMode ? (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Keep your account secure by using a strong password.
                </p>
                <button
                  onClick={() => setChangePasswordMode(true)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Change Password
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleChangePassword}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
                  >
                    Update Password
                  </button>
                  <button
                    onClick={() => {
                      setChangePasswordMode(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="px-4 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Privacy */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Privacy & Data</h2>
            </div>
          </div>
          <div className="p-6 space-y-3 text-sm">
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
              <p className="text-gray-700">Your data is encrypted and securely stored</p>
            </div>
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
              <p className="text-gray-700">Only authorized personnel can access your information</p>
            </div>
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
              <p className="text-gray-700">We comply with HIPAA and other privacy regulations</p>
            </div>
            <div className="pt-4 flex gap-4">
              <a href="/consent/privacy-policy" target="_blank" className="text-indigo-600 hover:text-indigo-700 flex items-center">
                Privacy Policy <ExternalLink className="w-3 h-3 ml-1" />
              </a>
              <a href="/consent/terms-of-use" target="_blank" className="text-indigo-600 hover:text-indigo-700 flex items-center">
                Terms of Use <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </EmployeePortalLayout>
  );
}
